// Generic 1D alignment-snap resolver — the "Canva smart guides" primitive,
// shared by every drag in the editor that's constrained to a plane (a panel
// sliding on its surface's (u, v), furniture/speakers/listener sliding on the
// room's (x, z) floor). Each of those is independently a pair of 1D snap
// solves, so the resolver itself never needs to know which plane it's on —
// callers project their own footprints onto one axis and hand this a flat
// candidate list.
//
// Deliberately dumb: nearest-candidate-within-threshold, no spatial index.
// Object counts in a room (tens, not thousands) never justify one.

export type SnapSourceKind =
  | 'object-edge' | 'object-center' | 'bounds-edge' | 'bounds-center' | 'mirror' | 'equal-spacing'
  | 'mirror-facet' | 'gap-mirror' | 'gap-center'
  | 'neighbor-angle' | 'wall-normal';

/** Lower sorts first — only consulted to break an exact-distance tie between
 *  two candidates (rare: two objects coincidentally equidistant). Distance
 *  is always the primary sort, matching how every "smart guide" tool actually
 *  behaves (closest wins, not "canvas edges always beat objects"). `mirror`
 *  and `equal-spacing` sort last: both are "smarter", less literal
 *  suggestions (a reflection, or a distance copied from elsewhere) rather
 *  than something physically at that position — see `useObjectDrag.ts`'s
 *  snap-target builders. `neighbor-angle` (another object's exact current
 *  yaw) sits with the literal object candidates; `wall-normal` (a cardinal
 *  angle relative to the nearest wall) sits with the structural/bounds ones
 *  — see ManipulationGizmo.tsx's rotation-ring snap.
 *
 *  `mirror-facet` (the same spot on a PARALLEL surface — the facing wall, the
 *  floor under a ceiling panel) deliberately sits in tier 0 with the literal
 *  object candidates rather than down with `mirror`. That's a product
 *  decision, not an oversight: a room's facing walls are the primary axis of
 *  symmetry in acoustic treatment, so "the same place on the other wall" is
 *  as literal a target as a neighbour's edge. The cost is that it competes on
 *  raw distance, which makes near-coincident candidates common enough that
 *  `dedupeCandidates` and `stickyNearestSnapCandidate` stop being optional —
 *  see their docs. */
const SOURCE_PRIORITY: Record<SnapSourceKind, number> = {
  'object-edge': 0,
  'object-center': 0,
  'neighbor-angle': 0,
  'mirror-facet': 0,
  'bounds-edge': 1,
  'bounds-center': 1,
  'wall-normal': 1,
  mirror: 2,
  'gap-center': 2,
  'gap-mirror': 3,
  'equal-spacing': 3,
};

/** Half-open interval on the snap axis, in the same coordinates as
 *  `SnapCandidate.value`. Carried by the spacing families so the guide
 *  overlay can draw the two distances being equalised without re-deriving
 *  them from the candidate list. */
export interface SnapSpan {
  from: number;
  to: number;
}

export interface SnapSource {
  kind: SnapSourceKind;
  /** The other entry this candidate came from — absent for a bounds candidate. */
  objectId?: string;
  /** The two equal distances this candidate creates, for the spacing
   *  families (`gap-mirror`, `gap-center`). Ordered low-to-high along the
   *  axis. A snap that means "these two gaps match" is illegible without
   *  drawing both of them — that's the whole difference between Canva's
   *  spacing guide and a bare alignment line. */
  spans?: readonly [SnapSpan, SnapSpan];
}

export interface SnapCandidate {
  value: number;
  source: SnapSource;
}

export interface SnapResult {
  /** `value` unchanged if nothing was within threshold. */
  value: number;
  snapped: boolean;
  candidate: SnapCandidate | null;
}

/** One coplanar object's footprint on a single snap axis (already projected
 *  to u/v or x/z by the caller — this module has no notion of 3D). */
export interface AxisFootprint {
  objectId: string;
  center: number;
  /** Half-extent along this axis. */
  half: number;
}

/** Edge + centre candidates for every footprint, plus the plane's own bounds
 *  (its two edges and its centre) if given.
 *
 *  The bounds EDGES are redundant for a caller whose downstream clamp already
 *  forces flush-to-boundary placement unconditionally (`clamp(v, lo, hi)`
 *  returns `lo` for every `v <= lo`, snapped or not — this is exactly panel-
 *  surface's `clampPose` on a wall, so a wall panel already lands flush
 *  without any help from this list; see the call-site note in
 *  useObjectDrag.ts's `snapTargets`). They still matter for a caller with a
 *  non-monotonic clamp (a polygon-shaped room's ceiling/floor bound, an
 *  L-room's notch) and for rendering a "you hit the boundary" guide line
 *  regardless of whether the clamp would have gotten there anyway. The
 *  bounds CENTRE has no such redundancy — it's a real, independent snap
 *  target (the surface/room centreline) either way. */
export function axisCandidates(
  footprints: AxisFootprint[],
  bounds?: { min: number; max: number },
): SnapCandidate[] {
  const out: SnapCandidate[] = [];
  if (bounds) {
    out.push({ value: bounds.min, source: { kind: 'bounds-edge' } });
    out.push({ value: bounds.max, source: { kind: 'bounds-edge' } });
    out.push({ value: (bounds.min + bounds.max) / 2, source: { kind: 'bounds-center' } });
  }
  for (const f of footprints) {
    out.push({ value: f.center, source: { kind: 'object-center', objectId: f.objectId } });
    out.push({ value: f.center - f.half, source: { kind: 'object-edge', objectId: f.objectId } });
    out.push({ value: f.center + f.half, source: { kind: 'object-edge', objectId: f.objectId } });
  }
  return out;
}

/** Equal-spacing candidates: continuing an already-evenly-spaced run of
 *  objects. Unlike `axisCandidates` (a static list built once at drag start),
 *  this depends on which two objects currently flank the drag point, so a
 *  caller re-derives it every move from the SAME footprint list.
 *
 *  Scope, deliberately narrow: this only offers "extend the existing gap on
 *  whichever side already has two neighbours in a row" (nearest neighbour L,
 *  and L's own next neighbour LL, give a reference gap; the candidate is
 *  where the dragged object would reproduce that same gap against L). It
 *  does NOT attempt "copy the gap between two neighbours elsewhere on the
 *  axis" or "centre exactly between two flanking objects" — both are real
 *  smart-guide behaviours in mature tools, but "continue the sequence" alone
 *  already covers the common case this app actually has (a row of evenly
 *  spaced panels/speakers) without the combinatorial candidate blow-up of a
 *  fully general version. */
export function equalSpacingCandidates(
  value: number,
  half: number,
  neighbors: AxisFootprint[],
): SnapCandidate[] {
  const out: SnapCandidate[] = [];
  const left = neighbors.filter((n) => n.center < value).sort((a, b) => b.center - a.center);
  const right = neighbors.filter((n) => n.center > value).sort((a, b) => a.center - b.center);

  if (left.length >= 2) {
    const [near, further] = left;
    const gap = (near.center - near.half) - (further.center + further.half);
    if (gap > 0) {
      out.push({
        value: near.center + near.half + gap + half,
        source: { kind: 'equal-spacing', objectId: near.objectId },
      });
    }
  }
  if (right.length >= 2) {
    const [near, further] = right;
    const gap = (further.center - further.half) - (near.center + near.half);
    if (gap > 0) {
      out.push({
        value: near.center - near.half - gap - half,
        source: { kind: 'equal-spacing', objectId: near.objectId },
      });
    }
  }
  return out;
}

/**
 * `axisCandidates`, but aware of how big the dragged object is.
 *
 * The distinction matters more than it looks. `axisCandidates` emits raw
 * positions — a neighbour's edge at u = 2 becomes a candidate at 2 — and the
 * resolver compares them against the dragged object's CENTRE. So "snap to the
 * TV's edge" lands the panel's centre on the TV's edge, i.e. overlapping the
 * TV by half the panel's width. That is not what anyone means by snapping to
 * an edge, and on a wall it is doubly wrong: `clampPose` forces `u >= hu`
 * unconditionally, so a bounds-edge candidate at u = 0 is not merely odd, it
 * is unreachable and silently dead.
 *
 * Every candidate here is therefore a position for the dragged object's
 * CENTRE that puts some feature of it somewhere meaningful:
 *
 *   centres aligned          value = other.center
 *   abutting, dragged left    value = other.lo - half
 *   abutting, dragged right   value = other.hi + half
 *   near edges flush          value = other.lo + half
 *   far edges flush           value = other.hi - half
 *
 * The two "flush" cases overlap the neighbour in this axis, which is correct
 * and wanted: a panel mounted above a TV with their left edges lined up is a
 * normal thing to want, and the two objects do not collide because they are
 * separated on the OTHER axis.
 *
 * `axisCandidates` stays as it is for the ground-plane drag, which has its own
 * clamp behaviour and its own shipped tests.
 */
export function alignmentCandidates(
  half: number,
  footprints: AxisFootprint[],
  bounds?: { min: number; max: number },
): SnapCandidate[] {
  const out: SnapCandidate[] = [];
  const push = (value: number, source: SnapSource) => {
    if (reachable(value, half, bounds)) out.push({ value, source });
  };
  if (bounds) {
    push(bounds.min + half, { kind: 'bounds-edge' });
    push(bounds.max - half, { kind: 'bounds-edge' });
    push((bounds.min + bounds.max) / 2, { kind: 'bounds-center' });
  }
  for (const f of footprints) {
    const lo = f.center - f.half;
    const hi = f.center + f.half;
    push(f.center, { kind: 'object-center', objectId: f.objectId });
    push(lo - half, { kind: 'object-edge', objectId: f.objectId });
    push(hi + half, { kind: 'object-edge', objectId: f.objectId });
    push(lo + half, { kind: 'object-edge', objectId: f.objectId });
    push(hi - half, { kind: 'object-edge', objectId: f.objectId });
  }
  return out;
}

// ── Spacing families ──────────────────────────────────────────────
//
// `axisCandidates` answers "line up WITH something". These two answer the
// question a user actually asks when placing treatment around a TV: "put it
// the same distance from that thing as the other one is", and "put it in the
// middle of the space that's left". Canva and Figma both surface these as
// spacing guides — matched pairs of capped segments with a distance readout —
// rather than as alignment lines, because the thing being matched is a
// LENGTH, not a position.
//
// Both are static for a whole drag (they depend only on the other objects'
// fixed positions and the dragged object's half-extent, never on where the
// cursor currently is), so callers build them once at drag start alongside
// `axisCandidates` rather than every pointermove the way `equalSpacing-
// Candidates` has to be.

/** One solid on the axis, in edge coordinates — a footprint or a plane
 *  boundary. Boundaries participate as zero-width solids so "the same gap
 *  from the wall's end as that panel has from the TV" needs no special case. */
interface Solid {
  lo: number;
  hi: number;
  objectId?: string;
}

function solidsFor(footprints: AxisFootprint[], bounds?: { min: number; max: number }): Solid[] {
  const solids: Solid[] = footprints.map((f) => ({
    lo: f.center - f.half,
    hi: f.center + f.half,
    objectId: f.objectId,
  }));
  if (bounds) {
    solids.push({ lo: bounds.min, hi: bounds.min });
    solids.push({ lo: bounds.max, hi: bounds.max });
  }
  return solids.sort((a, b) => (a.lo + a.hi) - (b.lo + b.hi));
}

/** Where a dragged object of half-extent `half` may legally sit, so a
 *  candidate that could only be reached by the downstream clamp overriding it
 *  is never emitted. An out-of-range candidate is worse than no candidate:
 *  the guide draws at the candidate and the object lands at the clamp, so the
 *  snap visibly lies about where it put things. */
function reachable(value: number, half: number, bounds?: { min: number; max: number }): boolean {
  if (!Number.isFinite(value)) return false;
  if (!bounds) return true;
  return value >= bounds.min + half - 1e-9 && value <= bounds.max - half + 1e-9;
}

/**
 * "Same distance from that edge as the other one" — the spacing snap.
 *
 * Two independent families, both emitted as `gap-mirror` because they render
 * identically (a pair of matched gaps) and a user cannot tell them apart:
 *
 *   • ADJACENT-PAIR copy. For each neighbouring pair of solids (A, B) with a
 *     positive gap `g` between them, the positions that reproduce `g` on the
 *     far side of A and on the far side of B. This is the TV case: a panel
 *     sitting `g` from the TV's left edge makes `g` from the TV's RIGHT edge
 *     a candidate.
 *   • BOUNDS-INSET mirror. A solid inset `d` from one end of the plane offers
 *     the same inset `d` from the other end — symmetric placement about the
 *     surface's centreline. This is NOT an adjacent-pair copy (the two ends
 *     are not adjacent to each other) and does not fall out of the first
 *     family; on a bare wall with one panel it is the only thing that fires.
 *
 * Gaps are measured EDGE TO EDGE, not centre to centre, so a wider panel sits
 * closer to the room's end rather than overhanging further. When the dragged
 * object and the reference object happen to be the same size the two readings
 * coincide, which is the common case and why the distinction is easy to miss.
 *
 * Restricted to ADJACENT pairs on purpose. Copying every gap to every solid's
 * far side is O(n²) candidates for no extra expressive power a user would
 * notice, and it crowds the axis badly enough that nothing feels free to move.
 */
export function gapMirrorCandidates(
  half: number,
  footprints: AxisFootprint[],
  bounds?: { min: number; max: number },
): SnapCandidate[] {
  const solids = solidsFor(footprints, bounds);
  const out: SnapCandidate[] = [];
  const push = (value: number, objectId: string | undefined, ref: SnapSpan, made: SnapSpan) => {
    if (!reachable(value, half, bounds)) return;
    const spans: readonly [SnapSpan, SnapSpan] = ref.from <= made.from ? [ref, made] : [made, ref];
    out.push({ value, source: { kind: 'gap-mirror', objectId, spans } });
  };

  for (let i = 0; i + 1 < solids.length; i++) {
    const a = solids[i];
    const b = solids[i + 1];
    const g = b.lo - a.hi;
    if (g <= 1e-9) continue;                    // overlapping or touching
    const ref: SnapSpan = { from: a.hi, to: b.lo };
    // Reproduce the gap past B (measuring from B, so B is the reference).
    push(b.hi + g + half, b.objectId, ref, { from: b.hi, to: b.hi + g });
    // ...and past A, on its far side.
    push(a.lo - g - half, a.objectId, ref, { from: a.lo - g, to: a.lo });
  }

  if (bounds) {
    for (const f of footprints) {
      const lo = f.center - f.half;
      const hi = f.center + f.half;
      const insetFromMin = lo - bounds.min;
      const insetFromMax = bounds.max - hi;
      if (insetFromMin > 1e-9) {
        push(
          bounds.max - insetFromMin - half,
          f.objectId,
          { from: bounds.min, to: lo },
          { from: bounds.max - insetFromMin, to: bounds.max },
        );
      }
      if (insetFromMax > 1e-9) {
        push(
          bounds.min + insetFromMax + half,
          f.objectId,
          { from: hi, to: bounds.max },
          { from: bounds.min, to: bounds.min + insetFromMax },
        );
      }
    }
  }
  return out;
}

/**
 * "Centre it in the space that's left" — the midpoint of every clear run
 * between two adjacent solids, plane boundaries included.
 *
 * A wall of 10 with a 6-wide TV centred on it leaves two 2-wide runs, so this
 * offers 1 and 9: exactly where a user putting a panel between the TV and the
 * wall's end wants it. `axisCandidates`' `bounds-center` is the degenerate
 * version of this for an empty plane; snapEngine's original doc called the
 * general case out as deliberately unimplemented.
 *
 * A run narrower than the dragged object emits nothing. The clamp would
 * override such a candidate anyway, and a guide drawn where the object cannot
 * go reads as a bug (see `reachable`).
 */
export function betweenCenterCandidates(
  half: number,
  footprints: AxisFootprint[],
  bounds?: { min: number; max: number },
): SnapCandidate[] {
  const solids = solidsFor(footprints, bounds);
  const out: SnapCandidate[] = [];
  for (let i = 0; i + 1 < solids.length; i++) {
    const lo = solids[i].hi;
    const hi = solids[i + 1].lo;
    const run = hi - lo;
    if (run <= 1e-9 || run < 2 * half - 1e-9) continue;
    const mid = (lo + hi) / 2;
    if (!reachable(mid, half, bounds)) continue;
    out.push({
      value: mid,
      source: {
        kind: 'gap-center',
        objectId: solids[i].objectId ?? solids[i + 1].objectId,
        spans: [{ from: lo, to: mid }, { from: mid, to: hi }],
      },
    });
  }
  return out;
}

/** Default coincidence radius for `dedupeCandidates`, metres. A millimetre is
 *  far below anything a user can express with a mouse but far above float
 *  noise, so it collapses "the same place, reached two ways" without ever
 *  merging two positions someone meant to distinguish. */
export const DEDUPE_EPSILON_M = 1e-3;

/**
 * Collapse candidates that name the same position, keeping the highest-
 * priority source.
 *
 * Necessary once more than about two families feed one axis, and necessary in
 * particular because `mirror-facet` sits in tier 0 (see SOURCE_PRIORITY): in a
 * symmetric room the mirror of a panel and a real neighbour's edge routinely
 * land within float noise of each other, and two candidates at "the same"
 * value that differ in the 15th decimal make the resolver's tie-break
 * meaningless — whichever the array happened to hold first wins, which is
 * effectively random and changes between frames.
 *
 * Returns candidates sorted by value, which also makes the output stable for
 * tests rather than dependent on the order families were concatenated in.
 */
export function dedupeCandidates(
  candidates: SnapCandidate[],
  epsilon: number = DEDUPE_EPSILON_M,
): SnapCandidate[] {
  const sorted = [...candidates].sort((a, b) => a.value - b.value);
  const out: SnapCandidate[] = [];
  for (const c of sorted) {
    const prev = out[out.length - 1];
    if (prev && Math.abs(c.value - prev.value) < epsilon) {
      if (SOURCE_PRIORITY[c.source.kind] < SOURCE_PRIORITY[prev.source.kind]) out[out.length - 1] = c;
      continue;
    }
    out.push(c);
  }
  return out;
}

/** Do two candidates name the same target? Identity for a snap candidate is
 *  (position, what it came from) — a `gap-mirror` and an `object-edge` that
 *  coincide this frame are still different targets, and rebuilding the list
 *  every move means object identity can't be carried by reference. */
export function sameCandidate(
  a: SnapCandidate | null,
  b: SnapCandidate | null,
  epsilon: number = DEDUPE_EPSILON_M,
): boolean {
  if (!a || !b) return false;
  return a.source.kind === b.source.kind
    && a.source.objectId === b.source.objectId
    && Math.abs(a.value - b.value) < epsilon;
}

/** How much further than `threshold` a already-held candidate keeps its lock.
 *  1.4 is roughly Figma's feel: firmly sticky, but a deliberate drag away
 *  still breaks it well before the object has travelled a visible distance. */
export const SNAP_STICKINESS = 1.4;

/**
 * `nearestSnapCandidate` with hysteresis — the resolver a live drag should
 * actually use.
 *
 * The plain resolver is stateless, so when two candidates sit close together
 * the held one changes the instant the cursor crosses their midpoint. At a
 * 60 Hz pointer with sub-pixel jitter that means the object visibly buzzes
 * between two positions and neither guide line settles. It is not a
 * theoretical problem here: `mirror-facet` at tier 0 puts a reflected
 * candidate near a real edge routinely, and `gap-mirror`/`gap-center` add
 * more positions to an axis that used to carry only edges and centres.
 *
 * Rule: while `held` is still on offer and the cursor is within
 * `threshold * stickiness` of it, it keeps the lock; otherwise fall through to
 * the plain nearest. So a lock is easy to acquire, sticky to hold, and breaks
 * cleanly once you drag away — and crucially the outcome depends on where the
 * drag has BEEN, which is what stops the buzz.
 *
 * Callers pass the previous call's `candidate` back in as `held`.
 */
export function stickyNearestSnapCandidate(
  value: number,
  candidates: SnapCandidate[],
  threshold: number,
  held: SnapCandidate | null,
  stickiness: number = SNAP_STICKINESS,
): SnapResult {
  if (held) {
    const stillOffered = candidates.find((c) => sameCandidate(c, held));
    if (stillOffered && Math.abs(value - stillOffered.value) < threshold * stickiness) {
      return { value: stillOffered.value, snapped: true, candidate: stillOffered };
    }
  }
  return nearestSnapCandidate(value, candidates, threshold);
}

/** The nearest candidate strictly within `threshold` of `value`, or `value`
 *  unchanged if none qualify. `threshold` is exclusive (a candidate exactly
 *  `threshold` away does not snap) so a caller can chain results without a
 *  candidate landing exactly on the boundary flip-flopping between frames. */
export function nearestSnapCandidate(
  value: number,
  candidates: SnapCandidate[],
  threshold: number,
): SnapResult {
  let best: SnapCandidate | null = null;
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = Math.abs(c.value - value);
    if (d >= threshold) continue;
    if (
      best === null
      || d < bestDist
      || (d === bestDist && SOURCE_PRIORITY[c.source.kind] < SOURCE_PRIORITY[best.source.kind])
    ) {
      best = c;
      bestDist = d;
    }
  }
  return best ? { value: best.value, snapped: true, candidate: best } : { value, snapped: false, candidate: null };
}

/** Converts a screen-space pixel radius to the equivalent world-space
 *  distance at `distanceToPlane` from the camera, for a standard vertical-
 *  FOV perspective camera.
 *
 *  This is the piece a 2D canvas tool never has to solve: Canva's catch
 *  radius is a few screen pixels regardless of zoom, converted to canvas
 *  units live, so snapping feels equally sticky whichever zoom level you're
 *  at. A free-orbiting 3D camera has no single "zoom level" — the same
 *  world-space radius (a fixed metres value, which is where this resolver
 *  started) is too sticky on an object close to the camera and imperceptible
 *  on one far away. Working from a pixel radius and re-deriving the world
 *  distance every frame, at the ACTUAL depth of whatever's being dragged,
 *  is the 3D-perspective equivalent of the same idea.
 *
 *  `distanceToPlane` is the camera-to-drag-point distance, not a true
 *  point-to-plane distance — a caller-side approximation, since a full
 *  point-to-plane projection needs the plane's normal and this module has no
 *  notion of 3D. Good enough for a catch-radius estimate; see the call site
 *  in useObjectDrag.ts. */
export function pixelRadiusToWorldThreshold(
  pixelRadius: number,
  distanceToPlane: number,
  viewportHeightPx: number,
  verticalFovRadians: number,
): number {
  if (viewportHeightPx <= 0 || distanceToPlane <= 0) return 0;
  const worldHeightAtDistance = 2 * distanceToPlane * Math.tan(verticalFovRadians / 2);
  return (pixelRadius * worldHeightAtDistance) / viewportHeightPx;
}

/** Shortest signed distance from `a` to `b` around a circle of circumference
 *  `period` (radians for an angle) — the usual `(b - a) mod period`, folded
 *  into `(-period/2, period/2]` so "1° short of wrapping around" reads as
 *  nearly zero instead of nearly a full turn. */
function wrappedDelta(a: number, b: number, period: number): number {
  let d = (b - a) % period;
  if (d > period / 2) d -= period;
  if (d <= -period / 2) d += period;
  return d;
}

/** `nearestSnapCandidate`'s counterpart for a circular value (a yaw angle):
 *  same nearest-within-threshold contract, but distance wraps at `period`
 *  (2π for radians) instead of growing without bound — so a candidate just
 *  past 0/2π correctly reads as close to a value just short of it. */
export function nearestAngleCandidate(
  value: number,
  candidates: SnapCandidate[],
  threshold: number,
  period: number = 2 * Math.PI,
): SnapResult {
  let best: SnapCandidate | null = null;
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = Math.abs(wrappedDelta(value, c.value, period));
    if (d >= threshold) continue;
    if (
      best === null
      || d < bestDist
      || (d === bestDist && SOURCE_PRIORITY[c.source.kind] < SOURCE_PRIORITY[best.source.kind])
    ) {
      best = c;
      bestDist = d;
    }
  }
  return best ? { value: best.value, snapped: true, candidate: best } : { value, snapped: false, candidate: null };
}
